import { useState } from "react";
import { DateRange } from "react-day-picker";
import { isValid } from "date-fns";
import {
  ArchiveSortOptions,
  TermFacetEntry,
  AlphabeticalFacetEntry,
} from "frontend/contentful/schema/semantics";
import { ReadonlyURLSearchParams, useSearchParams } from "next/navigation";
import { DirectoryFacet } from "../../toppers/directory-topper.props";

export type Facets = Record<string, Array<string>>;

export type ExtendedArchiveSortOptions =
  | ArchiveSortOptions
  | "lastNameString:asc"
  | "lastNameString:desc"
  | "HBSSortDate:asc"
  | "HBSSortDate:desc"
  | "HBSSortString:asc"
  | "HBSSortString:desc";

export const archiveSortsLegacy: Record<ArchiveSortOptions, string> = {
  "A — Z": "HBSSortString:asc",
  "Z — A": "HBSSortString:desc",
  Oldest: "HBSSortDate:asc",
  Newest: "HBSSortDate:desc",
};

export const archiveAlumniSorts: Record<ArchiveSortOptions, string> = {
  "A — Z": "lastNameString:asc",
  "Z — A": "lastNameString:desc",
  Oldest: "HBSSortDate:asc",
  Newest: "HBSSortDate:desc",
};

export const archiveSorts: Record<ArchiveSortOptions, string> = {
  "A — Z": "sortString:asc",
  "Z — A": "sortString:desc",
  Oldest: "sortDate:asc",
  Newest: "sortDate:desc",
};

export const alumniRecordStatusField = "recordStatusLabel";
export const alumniIncludeDeceasedLabel = "Include Deceased Alumni";
export const alumniIncludeDeceasedId = "includeDeceased";

const allSortMappings: Record<string, ExtendedArchiveSortOptions> = {};
Object.entries(archiveSorts).forEach(([label, value]) => {
  allSortMappings[value] = label as ExtendedArchiveSortOptions;
});
Object.entries(archiveAlumniSorts).forEach(([label, value]) => {
  allSortMappings[value] = label as ExtendedArchiveSortOptions;
});
Object.entries(archiveSortsLegacy).forEach(([label, value]) => {
  allSortMappings[value] = label as ExtendedArchiveSortOptions;
});
export const reverseSorts: Record<string, ExtendedArchiveSortOptions> =
  Object.fromEntries(
    Object.entries(allSortMappings).map(([label, value]) => [
      value,
      label as ExtendedArchiveSortOptions,
    ]),
  );
export function getInitialSelectedFacets(
  searchParams: ReadonlyURLSearchParams,
  facetEntries: Array<TermFacetEntry | AlphabeticalFacetEntry>,
  getKey?: (key: string) => string,
) {
  const initialSelectedFacets: Facets = {};

  facetEntries.forEach((facetEntry) => {
    const field = facetEntry.fields.field;
    if (!field) return;

    const values = searchParams.getAll(getKey ? getKey(field) : field);
    if (values.length > 0) {
      initialSelectedFacets[field] = values;
    }
  });

  return initialSelectedFacets;
}
export function getInitialSelectedSorts(
  searchParams: ReadonlyURLSearchParams,
  sortOptions: Array<ArchiveSortOptions | ExtendedArchiveSortOptions>,
  getKey?: (key: string) => string,
) {
  const initialSelectedSorts: Facets = {};

  const sortParamKey = getKey ? getKey("sort") : "sort";
  // Try the namespaced key first (if any), then plain "sort"
  const sortValue = searchParams.get(sortParamKey) ?? searchParams.get("sort");

  if (!sortValue) return initialSelectedSorts;

  const sortOption = reverseSorts[sortValue];
  if (sortOption !== undefined && sortOptions.includes(sortOption)) {
    initialSelectedSorts.sort = [sortValue];
  }
  return initialSelectedSorts;
}

export const useDateRange = (
  getKey: (key: string) => string = (key: string) => key,
) => {
  const searchParams = useSearchParams();
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const fromParam = searchParams.get(getKey("from"));
    const toParam = searchParams.get(getKey("to"));
    const parseYyyyMmDd = (s: string | null | undefined) => {
      if (!s) return undefined;
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
      if (!m) return undefined;
      const [, y, mo, d] = m;
      const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
      return isNaN(dt.getTime()) ? undefined : dt;
    };
    const fromDate = parseYyyyMmDd(fromParam);
    const toDate = parseYyyyMmDd(toParam);

    return {
      from: fromDate && isValid(fromDate) ? fromDate : undefined,
      to: toDate && isValid(toDate) ? toDate : undefined,
    };
  });

  const updateDateRangeUrlParams = (newDateRange: DateRange) => {
    const url = new URL(window.location.href);
    if (newDateRange?.from) {
      const from = newDateRange.from?.toISOString().split("T")[0];
      if (from) {
        url.searchParams.set(getKey("from"), from);
      }
    } else {
      url.searchParams.delete(getKey("from"));
    }
    if (newDateRange?.to) {
      const to = newDateRange.to?.toISOString().split("T")[0];
      if (to) {
        url.searchParams.set(getKey("to"), to);
      }
    } else {
      url.searchParams.delete(getKey("to"));
    }
    window.history.pushState({}, "", url);
  };

  const handleDatePickerSubmit = (newDateRange: DateRange) => {
    setDateRange(newDateRange);

    updateDateRangeUrlParams(newDateRange);
  };

  const getEsDateFilters = (filters: string, index?: string) => {
    if (dateRange.from || dateRange.to) {
      const prefix = filters ? " AND " : "";
      const fromUtc = dateRange.from
        ? new Date(
            Date.UTC(
              dateRange.from.getFullYear(),
              dateRange.from.getMonth(),
              dateRange.from.getDate(),
            ),
          ).getTime()
        : undefined;
      const toUtc = dateRange.to
        ? new Date(
            Date.UTC(
              dateRange.to.getFullYear(),
              dateRange.to.getMonth(),
              dateRange.to.getDate(),
            ),
          ).getTime()
        : undefined;
      const dateField = index === "protected" ? "HBSSortDate" : "sortDate";
      if (fromUtc && toUtc) {
        return `${prefix}${dateField}:[${fromUtc} TO ${toUtc}]`;
      } else if (fromUtc) {
        return `${prefix}${dateField}:>=${fromUtc}`;
      } else if (toUtc) {
        return `${prefix}${dateField}:<=${toUtc}`;
      }
    }
  };

  return {
    dateRange,
    resetDateRange: () => {
      setDateRange({ from: undefined, to: undefined });
      updateDateRangeUrlParams({ from: undefined, to: undefined });
    },
    handleDatePickerSubmit,
    getEsDateFilters,
  };
};

export function buildSearchURL({
  baseUrl,
  searchQuery,
  selectedFacets,
  selectedSorts,
  selectedPage,
  getKey,
  allFacets,
}: {
  baseUrl: string;
  searchQuery: string;
  selectedFacets: Record<string, Array<string>>;
  selectedSorts?: { sort?: Array<string> };
  selectedPage: number;
  getKey: (key: string) => string;
  allFacets: Array<DirectoryFacet>;
}) {
  const url = new URL(baseUrl);

  if (searchQuery) url.searchParams.set("q", searchQuery);
  else url.searchParams.delete("q");

  // Set all selected facets
  Object.entries(selectedFacets).forEach(([facet, values]) => {
    url.searchParams.delete(getKey(facet));
    values.forEach((value) => {
      url.searchParams.append(getKey(facet), value);
    });
  });

  // Set sort
  if (selectedSorts?.sort?.length) {
    url.searchParams.set(
      getKey("sort"),
      selectedSorts.sort[selectedSorts.sort.length - 1] ?? "",
    );
  } else {
    url.searchParams.delete(getKey("sort"));
  }

  // Set page
  if (selectedPage && selectedPage !== 1) {
    url.searchParams.set(getKey("page"), String(selectedPage));
  } else {
    url.searchParams.delete(getKey("page"));
  }

  // Add back any other existing params that are not allfacets, facets, sort, q, or page
  const existingParams = new URL(window.location.href).searchParams;
  existingParams.forEach((value, key) => {
    if (
      key !== getKey("q") &&
      key !== getKey("page") &&
      !Object.keys(selectedFacets)
        .map((facet) => getKey(facet))
        .includes(key) &&
      !allFacets?.map((facet) => getKey(facet.id)).includes(key)
    ) {
      url.searchParams.set(key, value);
    }
  });

  return url;
}
export const defaultStatusClause = `${alumniRecordStatusField}:("Active" OR "Lost" OR "Reported Deceased")`;

export function buildDirectoryFilterString(
  selectedFacets: Facets | undefined | null,
  options?: {
    enforceDefaultStatus?: boolean;
  },
): string {
  const { enforceDefaultStatus = true } = options ?? {};

  const deceasedStatusClause = `${alumniRecordStatusField}:("Active" OR "Lost" OR "Reported Deceased" OR "Deceased")`;

  if (!selectedFacets || Object.keys(selectedFacets).length === 0) {
    return enforceDefaultStatus ? `(${defaultStatusClause})` : "";
  }

  const clauses: Array<string> = [];
  const includeDeceasedValues = selectedFacets[alumniIncludeDeceasedId] || [];
  const includeDeceasedSelected = includeDeceasedValues.includes(
    alumniIncludeDeceasedLabel,
  );

  for (const [field, values] of Object.entries(selectedFacets)) {
    if (!values || values.length === 0) continue;

    if (field === alumniIncludeDeceasedId) {
      if (includeDeceasedSelected) {
        clauses.push(deceasedStatusClause);
      }
      continue;
    }

    clauses.push(`${field}:("${values.join('" OR "')}")`);
  }

  if (enforceDefaultStatus && !includeDeceasedSelected) {
    clauses.push(defaultStatusClause);
  }

  return clauses.length > 0 ? clauses.join(" AND ") : "";
}
